// 预约单管理（P1-10）前端 API：与现有交付 Worker 同一地址（REACT_APP_DELIVERY_API_URL）。
import { getDeliveryApiBase } from "./deliveryApi";
import { getAdminToken } from "./adminAuth";

export const BOOKING_DEFAULT_PAGE_SIZE = 20;
export const BOOKING_MAX_PAGE_SIZE = 50;

export function isBookingApiConfigured() {
    return Boolean(getDeliveryApiBase());
}

/**
 * 二维码 / 公开页指向的地址：基于当前站点 origin，复用 HashRouter 的 /#/b/:id。
 */
export function getBookingPublicUrl(id) {
    const base = window.location.origin + window.location.pathname;
    return `${base.replace(/\/+$/, "")}/#/b/${id}`;
}

async function parseError(response) {
    const body = await response.json().catch(() => ({}));
    const error = new Error(body.error?.message || "REQUEST_FAILED");
    error.status = response.status;
    return error;
}

function buildBookingsQuery({
    q,
    shootFrom,
    shootTo,
    page = 1,
    pageSize = BOOKING_DEFAULT_PAGE_SIZE,
} = {}) {
    const params = new URLSearchParams();
    if (q) {
        params.set("q", q);
    }
    if (shootFrom) {
        params.set("shootFrom", shootFrom);
    }
    if (shootTo) {
        params.set("shootTo", shootTo);
    }
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    return params.toString();
}

export async function createBooking({ title, location, shootAt, content, duration, notes }) {
    const base = getDeliveryApiBase();
    if (!base) {
        throw new Error("MISSING_API");
    }
    const token = getAdminToken();
    const response = await fetch(`${base}/api/booking`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title, location, shootAt, content, duration, notes }),
    });
    if (!response.ok) {
        throw await parseError(response);
    }
    const data = await response.json();
    return data.booking;
}

export async function listBookings(options = {}) {
    const base = getDeliveryApiBase();
    if (!base) {
        throw new Error("MISSING_API");
    }
    const token = getAdminToken();
    const query = buildBookingsQuery(options);
    const response = await fetch(`${base}/api/bookings?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        throw await parseError(response);
    }
    const data = await response.json();
    return {
        bookings: data.bookings || [],
        page: data.page || 1,
        pageSize: data.pageSize || BOOKING_DEFAULT_PAGE_SIZE,
        hasMore: Boolean(data.hasMore),
    };
}

export async function getBooking(id) {
    const base = getDeliveryApiBase();
    if (!base) {
        const error = new Error("MISSING_API");
        error.debug = { stage: "config", message: "REACT_APP_DELIVERY_API_URL 未配置（apiBase 为空）" };
        throw error;
    }
    // 防止微信内置浏览器（X5/XWeb）缓存旧的 404/响应：禁用缓存并加时间戳打破缓存。
    const bust = `_=${Date.now()}`;
    const url = `${base}/api/booking/${encodeURIComponent(id)}?${bust}`;

    let response;
    try {
        // 只用 cache:"no-store" + URL 时间戳破缓存；不加自定义请求头，避免触发 CORS 预检（微信 WKWebView 会因预检失败报 "Load failed"）。
        response = await fetch(url, { cache: "no-store" });
    } catch (networkError) {
        // fetch 直接抛出 = 网络层失败（DNS / 跨域被拦 / 断网等），微信里最常见。
        const error = new Error("NETWORK_ERROR");
        error.debug = {
            stage: "network",
            url,
            message: networkError && networkError.message ? networkError.message : String(networkError),
        };
        throw error;
    }

    const rawBody = await response.text().catch(() => "");
    let parsedBody = null;
    try {
        parsedBody = rawBody ? JSON.parse(rawBody) : null;
    } catch {
        parsedBody = null;
    }

    if (!response.ok) {
        const error = new Error(parsedBody?.error?.message || "REQUEST_FAILED");
        error.status = response.status;
        error.debug = {
            stage: "http",
            url,
            status: response.status,
            statusText: response.statusText,
            body: rawBody,
        };
        throw error;
    }

    return parsedBody ? parsedBody.booking : null;
}

export async function deleteBooking(id) {
    const base = getDeliveryApiBase();
    if (!base) {
        throw new Error("MISSING_API");
    }
    const token = getAdminToken();
    const response = await fetch(`${base}/api/booking/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        throw await parseError(response);
    }
    return true;
}
